import {
  Box,
  capitalize,
  IconButton,
  Stack,
  styled,
  TextField,
} from "@mui/material";
import { customColors } from "models/customColors";
import { ReactComponent as MessagesIcon } from "images/messages.svg";
import { useContext, useEffect, useRef, useState } from "react";
import { MovableModal } from "./MovableModal";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { API } from "aws-amplify";
import { ReactComponent as SendIcon } from "images/send-message-icon.svg";
import { ModalSpinner } from "./ModalSpinner";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { Message } from "./Message";
import { GlobalContext } from "store/globalContext";
import { useSearchParams } from "react-router-dom";

const MESSAGE_RELOAD_INTERVAL = 5000;

const Container = styled(Box)`
  position: fixed;
  padding: 4px 16px;
  width: fit-content;
  height: 46px;
  bottom: 20px;
  right: 20px;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  font-size: 14px;

  border-radius: 12px;
  color: white;
  background-color: ${customColors.mainColor};

  &:hover {
    background-color: ${customColors.mainColorHover};
    cursor: pointer;
  }
`;

const MessagesContainer = styled(Stack)`
  padding: 10px 0 20px 0;
  flex: 1 1 auto;

  justify-content: flex-start;
  align-items: center;

  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  background-color: ${customColors.messageBackground};
`;

const NewMessageForm = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  padding: 10px 20px;
  flex: 0 0 auto;
  height: fit-content;
  color: white;
  background-color: ${customColors.black};
`;

const Header = styled(Stack)``;

const Title = styled(Stack)`
  font-size: 1rem;
`;

const Name = styled(Box)`
  font-weight: bold;
`;

const Contact = styled(Box)`
  font-size: 0.7rem;
  color: ${customColors.grayText};
`;

const Avatar = styled(Stack)`
  flex: 0 0 auto;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;

  font-size: 1.5rem;

  border-radius: 50%;
  color: white;
  background-color: ${customColors.mainColor};
`;

const SendButton = styled(IconButton)`
  background-color: ${customColors.mainColor};
  &:hover {
    background-color: ${customColors.mainColorHover};
  }
  border-radius: 10px;
`;

const getCapitalizedInitials = (str) => {
  if (!str) return "";

  return str
    .split(" ")
    .map((word) => (word?.length ? word[0] : "").toUpperCase())
    .join("");
};

export const ReferralMessages = ({ data, admins, marketers, schedulers }) => {
  const [searchParams] = useSearchParams();
  const command = searchParams.get("c");

  const { createApiCall } = useCreateApiCall();
  const { userData } = useContext(GlobalContext);
  const isSuperAdmin =
    userData?.role === "Super Admin" ||
    userData?.email === "admin@referral-stream.com";

  const dispatch = useAppDispatch();
  const [isMessagesOpened, setIsMessagesOpened] = useState(false);
  const [prevMessages, setPrevMessages] = useState(null);
  const [messages, setMessages] = useState(null);
  const messagesContainer = useRef(null);
  const inputRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const {
    originatorName,
    originatorJobTitle,
    originatorPhone,
    originatorEmail,
    question,
    createdAt,
  } = data;

  const sendNewMessage = (data) =>
    createApiCall(async () => {
      const referralId = data?.id;
      if (!referralId) return;

      const message = inputRef.current.value;
      if (!message) return;

      setIsSending(true);

      const result = await API.post("restApiV2", `/messages`, {
        body: {
          message,
          referralId,
          dashboardAccessToken: null,
        },
      });

      if (result?.error) {
        console.error(result.error);
        dispatch(showError(result?.message || result.error));
        setIsSending(false);
        return;
      }

      setMessages((prevMessages) => [...(prevMessages ?? []), result]);
      inputRef.current.value = "";
      setIsSending(false);
    });

  const scrollToBottom = () => {
    if (!messagesContainer.current) return;
    const height = messagesContainer.current.scrollHeight;

    messagesContainer.current.scrollTop = height;
  };

  const loadData = (data) =>
    createApiCall(
      async () => {
        const referralId = data?.id;
        if (!referralId) return;

        const result = await API.post("restApiV2", `/messages/all-messages`, {
          body: {
            referralId,
            dashboardAccessToken: null,
          },
        });

        if (result?.error) {
          console.error(result.error);
          dispatch(showError(result?.message || result.error));
          setMessages([]);
          setIsLoading(false);
          return;
        }
        const messages = result ?? [];

        setMessages(messages);
        setIsLoading(false);
      },
      (error) => {
        dispatch(showError(error));
        setIsLoading(false);
        setMessages([]);
      },
      true
    );

  useEffect(() => {
    let interval;

    if (isMessagesOpened) {
      scrollToBottom();
      loadData(data)();

      interval = setInterval(() => {
        loadData(data)();
      }, MESSAGE_RELOAD_INTERVAL);
    }

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMessagesOpened, data]);

  useEffect(() => {
    if (prevMessages?.length === messages?.length) return;
    scrollToBottom();
    setPrevMessages(messages);
  }, [messages, prevMessages]);

  useEffect(() => {
    if (!command) {
      return;
    }

    if (command === "messages") {
      setIsMessagesOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command, data]);

  const originatorId = data?.originatorId;
  const isSendingDisabled =
    isSending ||
    isSuperAdmin ||
    ["Completed", "Canceled"].includes(data?.status);

  return (
    <>
      <Container onClick={() => setIsMessagesOpened(true)}>
        Message Sender <MessagesIcon />
      </Container>
      {isMessagesOpened && isLoading && messages === null && (
        <ModalSpinner open />
      )}
      <MovableModal
        isOpen={isMessagesOpened}
        title={
          <Header direction={"row"} alignItems={"center"} gap={1}>
            <Avatar>{getCapitalizedInitials(originatorName)}</Avatar>
            <Title justifyContent={"center"} alignItems={"flex-start"} gap={0}>
              <Name>{originatorName}</Name>
              <Contact>
                {[
                  capitalize(originatorJobTitle ?? ""),
                  originatorPhone,
                  originatorEmail,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Contact>
            </Title>
          </Header>
        }
        onClose={() => setIsMessagesOpened(false)}
      >
        <MessagesContainer ref={messagesContainer}>
          {messages?.length === 0 && !question && (
            <Stack flex={1} justifyContent={"center"} alignItems={"center"}>
              No messages
            </Stack>
          )}
          {!!question && (
            <Message
              key={question.id}
              createdAt={createdAt}
              creatorId={originatorId}
              message={question}
              originatorId={originatorId}
              originatorName={originatorName}
              admins={admins}
              marketers={marketers}
              schedulers={schedulers}
              userData={userData}
            />
          )}
          {messages === null && (
            <Stack flex={1} justifyContent={"center"} alignItems={"center"}>
              Loading...
            </Stack>
          )}
          {messages?.map(({ id, createdAt, creatorId, message }) => (
            <Message
              key={id}
              {...{
                createdAt,
                creatorId,
                message,
                originatorId,
                originatorName,
                userData,
                admins,
                marketers,
                schedulers,
              }}
            />
          ))}
        </MessagesContainer>
        <NewMessageForm>
          <TextField
            placeholder="Type your message here"
            multiline
            fullWidth
            variant="outlined"
            size="small"
            inputRef={inputRef}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
            disabled={isSendingDisabled}
          />
          <SendButton
            disabled={isSendingDisabled}
            onClick={() => sendNewMessage(data)()}
          >
            <SendIcon />
          </SendButton>
        </NewMessageForm>
      </MovableModal>
    </>
  );
};
